import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import store from 'store';

import { useSearchParams } from '../use-search-params';

import type { UpdatedStartType } from 'api/endpoints/search.types';
import type { Dispatch } from 'store/store.types';

function toggleFilter(id: number, filters: Set<number> | null | undefined) {
  if (filters?.has(id)) {
    filters.delete(id);
  } else {
    filters = new Set(filters);
    filters.add(id);
  }
  return filters;
}

const selectSearchFilters = store.select(
  ({ search: { allBoardFilters, allTagFilters } }) => ({
    allBoardFilters,
    allTagFilters,
  }),
);

export default function useSearchControls() {
  const { setTerms: setTermsInPath, setQuery } = useSearchParams();
  const {
    search: {
      getResults,
      setLastUpdatedFilter: setLastUpdatedInModel,
      setBoardFilters: setBoardsInModel,
      setTagFilters: setTagsInModel,
    },
  } = useDispatch<Dispatch>();
  const { allBoardFilters, allTagFilters } = useSelector(selectSearchFilters);
  const selectAllBoardFilters = useCallback(() => {
    const newBoardFilters = new Set(allBoardFilters);
    setBoardsInModel(newBoardFilters);
    setQuery?.({ boards: newBoardFilters });
  }, [allBoardFilters, setBoardsInModel, setQuery]);
  const selectAllTagFilters = useCallback(() => {
    const newTagFilters = new Set(allTagFilters);
    setTagsInModel(newTagFilters);
    setQuery?.({ tags: newTagFilters });
  }, [allTagFilters, setTagsInModel, setQuery]);
  return {
    ...useMemo(() => {
      const toggleTagFilter =
        (id: number) => (tags: Set<number> | null | undefined) => {
          const newTagFilters = toggleFilter(id, tags);
          setTagsInModel(newTagFilters);
          return newTagFilters;
        };

      return {
        setTerms: (searchTerms: string) => {
          getResults({ searchTerms }); // todo Consider replacing getResults with setTermsInModel ?
          setTermsInPath?.(searchTerms);
        },
        setLastUpdatedFilter: (lastUpdated: UpdatedStartType | null) => {
          setLastUpdatedInModel(lastUpdated);
          setQuery?.({ lastUpdated });
        },
        toggleBoardFilter: (id: number) =>
          setQuery?.(({ boards }) => {
            const newBoardFilters = toggleFilter(id, boards);
            setBoardsInModel(newBoardFilters);
            return { boards: newBoardFilters };
          }),
        clearBoardFilters: () => {
          setBoardsInModel(new Set());
          setQuery?.({ boards: new Set() });
        },
        addTagFilter: (id: number) =>
          setQuery?.(({ tags }) => {
            if (tags?.has(id)) {
              // do nothing
              return { tags };
            }
            return { tags: toggleTagFilter(id)(tags) };
          }),
        toggleTagFilter: (id: number) =>
          setQuery?.(({ tags }) => ({ tags: toggleTagFilter(id)(tags) })),
        clearTagFilters: () => {
          setTagsInModel(new Set());
          setQuery?.({ tags: new Set() });
        },
      };
    }, [
      setTagsInModel,
      getResults,
      setTermsInPath,
      setLastUpdatedInModel,
      setQuery,
      setBoardsInModel,
    ]),
    selectAllBoardFilters,
    selectAllTagFilters,
  };
}
